import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getFollowUpById } from '../../lib/services/formService';
import {
  FollowupFormData,
  formatDiarySection,
  formatHeadacheSummarySection,
  formatPainIntensitySection,
  formatAttackDurationSection,
  formatSideEffectSection,
  formatStoppedMedicationSection,
  formatNewMedicationSection,
  formatNewConditionSection,
  formatOtherImportantInfoSection,
  formatBloodPressureSection,
  formatPrescriptionRenewalSection,
  formatMidasSection,
  formatEstimatedHeadacheDays,
  formatMidasPainSection,
  formatHITSection,
} from '../../lib/helpers/followupFormFormatters';
import SectionCard from './SectionCard';
import JournalHeader from './JournalHeader';
import { DS } from './designTokens';
import { formatFormStatus } from './formUtils';
import { reportError } from '../../lib/sentry';
import JournalCopyPanel from './JournalCopyPanel';

interface FollowUpJournalProps {
  patient: { _id: string };
  formId: string;
  onBack: () => void;
}

const FollowUpJournal: React.FC<FollowUpJournalProps> = ({
  patient,
  formId,
  onBack,
}) => {
  const [loading, setLoading] = useState(true);
  const [formStatus, setFormStatus] = useState('Ej påbörjad');
  const [status, setStatus] = useState('pending');
  const [formData, setFormData] = useState<FollowupFormData | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getFollowUpById(patient._id, formId);
        const { form } = res.data.result;
        if (form) {
          setFormStatus(formatFormStatus(form.status, form.submittedAt));
          setStatus(form.status);
          setFormData(form.formData as FollowupFormData);
        }
      } catch (err) {
        reportError(err, { tags: { area: 'followup_journal' } });
        setFormStatus('Fel vid hämtning');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [patient._id, formId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: DS.deepTeal }} />
      </Box>
    );
  }

  if (!formData) {
    return (
      <JournalHeader
        title="Återbesök – Patientsvar"
        status={status}
        formStatus={formStatus}
        onBack={onBack}
      />
    );
  }

  const diary = formatDiarySection(formData);
  const headacheSummary = formatHeadacheSummarySection(formData);
  const painIntensity = formatPainIntensitySection(formData);
  const attackDuration = formatAttackDurationSection(formData);
  const sideEffect = formatSideEffectSection(formData);
  const stoppedMed = formatStoppedMedicationSection(formData);
  const newMed = formatNewMedicationSection(formData);
  const newCondition = formatNewConditionSection(formData);
  const otherInfo = formatOtherImportantInfoSection(formData);
  const bloodPressure = formatBloodPressureSection(formData);
  const prescription = formatPrescriptionRenewalSection(formData);
  const midas = formatMidasSection(formData);
  const estimatedDays = formatEstimatedHeadacheDays(formData);
  const midasPain = formatMidasPainSection(formData);
  const hit = formatHITSection(formData);

  const hasAssessment = headacheSummary || painIntensity || attackDuration;
  const hasMidas = midas || estimatedDays || midasPain;

  return (
    <Box sx={{ fontFamily: DS.fontBody, maxWidth: 720 }}>
      <JournalHeader
        title="Återbesök – Patientsvar"
        status={status}
        formStatus={formStatus}
        onBack={onBack}
      />

      <JournalCopyPanel>
        {diary && (
          <SectionCard title="Dagbok">
            <p>{diary}</p>
          </SectionCard>
        )}

        {hasAssessment && (
          <SectionCard title="Aktuellt">
            {headacheSummary && <p>{headacheSummary}</p>}
            {painIntensity && <p>{painIntensity}</p>}
            {attackDuration && <p>{attackDuration}</p>}
          </SectionCard>
        )}

        {(sideEffect || stoppedMed || newMed) && (
          <SectionCard title="Behandling">
            {sideEffect && <p>{sideEffect}</p>}
            {stoppedMed && <p>{stoppedMed}</p>}
            {newMed && <p>{newMed}</p>}
          </SectionCard>
        )}

        {(newCondition || otherInfo) && (
          <SectionCard title="Övrigt">
            {newCondition && <p>{newCondition}</p>}
            {otherInfo && <p>{otherInfo}</p>}
          </SectionCard>
        )}

        {(hasMidas || hit) && (
          <>
            <SectionCard title="Midas">
              {estimatedDays && <p>{estimatedDays}</p>}
              {midasPain && <p>{midasPain}</p>}
              {midas && <p>{midas}</p>}
            </SectionCard>
            {hit && (
              <SectionCard title="HIT-6">
                <p>{hit}</p>
              </SectionCard>
            )}
          </>
        )}

        {bloodPressure && (
          <SectionCard title="Blodtryck">
            <p>{bloodPressure}</p>
          </SectionCard>
        )}

        {prescription && (
          <SectionCard title="Recept">
            <p>{prescription}</p>
          </SectionCard>
        )}
      </JournalCopyPanel>
    </Box>
  );
};

export default FollowUpJournal;
